/**
 * Curve evaluation for osu! slider paths.
 *
 * A slider's `[HitObjects]` row encodes its geometry as
 *   `<curveType>|<x>:<y>|<x>:<y>|...`
 * where `curveType` is one of:
 *   - `B` — piecewise Bezier (most common in modern maps). Repeated
 *     consecutive control points act as **anchors** that split the
 *     path into independent Bezier segments.
 *   - `L` — linear: straight segments between consecutive control
 *     points.
 *   - `P` — perfect circle: arc through three points (head + two
 *     control points). Degenerate (collinear) input falls back to
 *     linear.
 *   - `C` — Catmull-Rom: legacy spline format. Each consecutive
 *     4-tuple of control points defines a Catmull-Rom segment with
 *     endpoint duplication for boundary conditions. Rare in modern
 *     maps but still seen in older ranked sets.
 *
 * The full effective control-point list for evaluation is the
 * slider's head position followed by the control points from the
 * row (so a `B|100:100` slider with head (50,50) evaluates as a
 * 2-point Bezier from (50,50) to (100,100), i.e. a straight line).
 *
 * The output is a sampled polyline (`SliderPath`) that downstream
 * code (engine + renderer) walks directly:
 *   - `points`: ordered (x, y) samples along the curve.
 *   - `cumulativeLength`: arc-length at each sample (`points[i]`
 *     sits `cumulativeLength[i]` units from the head).
 *   - `length`: total arc-length of the sampled polyline (≤ the
 *     `length` field on the .osu row, since osu mappers can specify
 *     a `length` shorter than the geometric path to truncate the
 *     slider; the renderer / engine should clamp distance queries
 *     to `min(querydistance, length)`).
 *
 * The renderer and engine both call {@link pointAtDistance} which
 * walks `cumulativeLength` to find the segment containing a given
 * arc-length, then linearly interpolates between the neighbouring
 * samples. With ~32-64 samples per segment that's well under 1px
 * error at osu's 512×384 playfield resolution.
 *
 * All inputs and outputs are in osu playfield units (the same
 * coordinate space as `obj.x` / `obj.y` on hit objects).
 */

export type SliderCurveType = 'L' | 'B' | 'P' | 'C';

export interface Vec2 {
  x: number;
  y: number;
}

export interface SliderPath {
  /** Sampled (x, y) points along the curve, head-first. */
  points: Vec2[];
  /**
   * Cumulative arc-length at each sample. `cumulativeLength[i]` is
   * the geometric distance from `points[0]` to `points[i]` along
   * the polyline. Always non-negative, monotonically non-decreasing,
   * and `cumulativeLength[0] === 0`.
   */
  cumulativeLength: number[];
  /** Total arc-length of the sampled polyline. */
  length: number;
}

/**
 * Number of samples per Bezier / Catmull segment. 64 keeps
 * sub-pixel accuracy at osu's playfield resolution (a typical
 * slider segment is < 200 osu units long; 200/64 ≈ 3 units
 * between samples, and the linear interp between them sits well
 * under one rendered pixel).
 */
const SAMPLES_PER_SEGMENT = 64;

/**
 * Sample density for circular arcs. We pick a sample count
 * proportional to the arc's angular extent so very short arcs
 * don't waste samples and very long ones stay smooth.
 */
const SAMPLES_PER_RADIAN = 24;
const MIN_ARC_SAMPLES = 8;

/**
 * Build a sampled polyline for the given curve type and control
 * points. The first control point is the slider head; the rest are
 * the values from the .osu row's `<x>:<y>|...` payload.
 *
 * Empty / single-point input returns a degenerate path with a
 * single sample at the head and zero length — callers should treat
 * that as "render head only, no slider body" (which matches the
 * graceful-degradation behaviour the renderer wants for malformed
 * source data).
 */
export function buildSliderPath(
  curveType: SliderCurveType,
  controlPoints: ReadonlyArray<Vec2>,
): SliderPath {
  if (controlPoints.length === 0) {
    return emptyPath({ x: 0, y: 0 });
  }
  if (controlPoints.length === 1) {
    return emptyPath(controlPoints[0]);
  }
  switch (curveType) {
    case 'L':
      return buildLinearPath(controlPoints);
    case 'B':
      return buildBezierPath(controlPoints);
    case 'P':
      return buildPerfectCirclePath(controlPoints);
    case 'C':
      return buildCatmullPath(controlPoints);
  }
}

/**
 * Look up the (x, y) position at a given arc-length along the
 * sampled polyline. Distances < 0 return the head; distances >
 * `path.length` return the tail. Linear interpolation between
 * consecutive samples — accuracy is bounded by sample density
 * (see {@link SAMPLES_PER_SEGMENT}).
 *
 * Returns a fresh `Vec2` so callers can stash it without aliasing
 * the path's sample array.
 */
export function pointAtDistance(path: SliderPath, distance: number): Vec2 {
  const n = path.points.length;
  if (n === 0) {
    return { x: 0, y: 0 };
  }
  if (n === 1 || distance <= 0) {
    return { x: path.points[0].x, y: path.points[0].y };
  }
  if (distance >= path.length) {
    return { x: path.points[n - 1].x, y: path.points[n - 1].y };
  }
  // Binary search the cumulative-length array for the first sample
  // whose distance ≥ target. O(log n) — cheap, but the input is
  // typically only a few hundred entries so a linear walk would
  // also be fine; binary keeps follow-circle queries fast under
  // very long sliders.
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (path.cumulativeLength[mid] < distance) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  const i = lo;
  const prev = path.cumulativeLength[i - 1];
  const next = path.cumulativeLength[i];
  const span = next - prev;
  // Degenerate case: two samples at the exact same arc-length (can
  // happen at Bezier anchor points where two segments meet without
  // any geometric distance between their joining samples). Return
  // the previous sample to avoid 0/0.
  if (span <= 0) {
    return { x: path.points[i - 1].x, y: path.points[i - 1].y };
  }
  const t = (distance - prev) / span;
  return {
    x: path.points[i - 1].x + (path.points[i].x - path.points[i - 1].x) * t,
    y: path.points[i - 1].y + (path.points[i].y - path.points[i - 1].y) * t,
  };
}

/**
 * Squared distance from a point to the closest point on the sampled
 * polyline. Used by the engine's follow-circle test — returning
 * squared distance lets callers compare against `radius*radius`
 * without a `sqrt`. Returns `+Infinity` for an empty path.
 */
export function distanceSqToPath(path: SliderPath, p: Vec2): number {
  const n = path.points.length;
  if (n === 0) {
    return Number.POSITIVE_INFINITY;
  }
  if (n === 1) {
    const dx = path.points[0].x - p.x;
    const dy = path.points[0].y - p.y;
    return dx * dx + dy * dy;
  }
  let best = Number.POSITIVE_INFINITY;
  for (let i = 1; i < n; i++) {
    const ax = path.points[i - 1].x;
    const ay = path.points[i - 1].y;
    const bx = path.points[i].x;
    const by = path.points[i].y;
    const dSq = pointToSegmentDistanceSq(p.x, p.y, ax, ay, bx, by);
    if (dSq < best) {
      best = dSq;
    }
  }
  return best;
}

function pointToSegmentDistanceSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq <= 0) {
    const ex = px - ax;
    const ey = py - ay;
    return ex * ex + ey * ey;
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  if (t < 0) {
    t = 0;
  } else if (t > 1) {
    t = 1;
  }
  const cx = ax + dx * t;
  const cy = ay + dy * t;
  const ex = px - cx;
  const ey = py - cy;
  return ex * ex + ey * ey;
}

function emptyPath(p: Vec2): SliderPath {
  return {
    points: [{ x: p.x, y: p.y }],
    cumulativeLength: [0],
    length: 0,
  };
}

function buildLinearPath(cps: ReadonlyArray<Vec2>): SliderPath {
  const points: Vec2[] = [{ x: cps[0].x, y: cps[0].y }];
  const cumulative: number[] = [0];
  let total = 0;
  for (let i = 1; i < cps.length; i++) {
    const a = cps[i - 1];
    const b = cps[i];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const segLen = Math.hypot(dx, dy);
    if (segLen <= 0) {
      continue;
    }
    total += segLen;
    points.push({ x: b.x, y: b.y });
    cumulative.push(total);
  }
  return { points, cumulativeLength: cumulative, length: total };
}

/**
 * Bezier path: split the control-point list at every anchor (a
 * point that equals its predecessor) into independent sub-paths,
 * evaluate each as a Bezier of arbitrary degree via de Casteljau,
 * concatenate.
 *
 * osu! mappers use anchors to chain multiple Bezier segments into
 * a single slider while keeping each segment's degree manageable
 * (usually quadratic or cubic). A row like
 *   `B|326:71|326:71|343:105|343:105|337:153`
 * with head (332,24) means three quadratic Beziers chained at
 * (326,71) and (343,105).
 */
function buildBezierPath(cps: ReadonlyArray<Vec2>): SliderPath {
  const segments: Vec2[][] = [];
  let current: Vec2[] = [cps[0]];
  for (let i = 1; i < cps.length; i++) {
    const cp = cps[i];
    const prev = cps[i - 1];
    if (cp.x === prev.x && cp.y === prev.y && current.length >= 2) {
      // Anchor — close the current segment, start a new one at this
      // duplicated point. Skip the duplicated CP itself: the next
      // segment will start from the same coordinate but treat it as
      // a new endpoint.
      segments.push(current);
      current = [cp];
      continue;
    }
    current.push(cp);
  }
  if (current.length >= 2) {
    segments.push(current);
  }
  if (segments.length === 0) {
    return emptyPath(cps[0]);
  }
  const points: Vec2[] = [{ x: segments[0][0].x, y: segments[0][0].y }];
  const cumulative: number[] = [0];
  let total = 0;
  for (const seg of segments) {
    if (seg.length === 2) {
      // Degree 1 (linear) — one straight edge, no need for de Casteljau.
      const a = seg[0];
      const b = seg[1];
      const segLen = Math.hypot(b.x - a.x, b.y - a.y);
      if (segLen > 0) {
        total += segLen;
        points.push({ x: b.x, y: b.y });
        cumulative.push(total);
      }
      continue;
    }
    // Degree ≥ 2 — sample the Bezier at SAMPLES_PER_SEGMENT positions
    // (skip t=0, it's already in `points` from the previous segment's
    // tail or the path origin).
    let prev = seg[0];
    for (let s = 1; s <= SAMPLES_PER_SEGMENT; s++) {
      const t = s / SAMPLES_PER_SEGMENT;
      const sample = evaluateBezier(seg, t);
      const segLen = Math.hypot(sample.x - prev.x, sample.y - prev.y);
      total += segLen;
      points.push(sample);
      cumulative.push(total);
      prev = sample;
    }
  }
  return { points, cumulativeLength: cumulative, length: total };
}

/**
 * de Casteljau's algorithm for arbitrary-degree Bezier evaluation.
 * Allocates a working buffer of `cps.length` Vec2s and iteratively
 * collapses it. O(n²) in the degree, fine for slider segments
 * which are almost always degree 2-4.
 */
function evaluateBezier(cps: ReadonlyArray<Vec2>, t: number): Vec2 {
  const xs = new Float64Array(cps.length);
  const ys = new Float64Array(cps.length);
  for (let i = 0; i < cps.length; i++) {
    xs[i] = cps[i].x;
    ys[i] = cps[i].y;
  }
  for (let r = 1; r < cps.length; r++) {
    for (let i = 0; i < cps.length - r; i++) {
      xs[i] = xs[i] * (1 - t) + xs[i + 1] * t;
      ys[i] = ys[i] * (1 - t) + ys[i + 1] * t;
    }
  }
  return { x: xs[0], y: ys[0] };
}

/**
 * Perfect circle (P): arc through three points (head, mid, tail).
 * If the points are collinear, falls back to linear evaluation
 * (matches osu! lazer behaviour — degenerate Ps are treated as
 * 3-point linear paths).
 *
 * P sliders with > 3 control points are technically malformed in
 * the .osu spec but observed in the wild; we fall back to Bezier
 * for those (lazer's behaviour).
 */
function buildPerfectCirclePath(cps: ReadonlyArray<Vec2>): SliderPath {
  if (cps.length !== 3) {
    return buildBezierPath(cps);
  }
  const a = cps[0];
  const b = cps[1];
  const c = cps[2];
  const centre = circumcentre(a, b, c);
  if (centre === null) {
    return buildLinearPath(cps);
  }
  const radius = Math.hypot(a.x - centre.x, a.y - centre.y);
  const startAngle = Math.atan2(a.y - centre.y, a.x - centre.x);
  let endAngle = Math.atan2(c.y - centre.y, c.x - centre.x);
  let midAngle = Math.atan2(b.y - centre.y, b.x - centre.x);
  // Pick the sweep direction so the arc passes through `b`. We
  // unwrap `endAngle` into the same revolution as `startAngle`,
  // then flip if `midAngle` doesn't land between them.
  endAngle = unwrapAngle(endAngle, startAngle);
  midAngle = unwrapAngle(midAngle, startAngle);
  // If midAngle isn't between start and end, flip end through a
  // full revolution.
  const between = (midAngle - startAngle) * (endAngle - startAngle) > 0
    && Math.abs(midAngle - startAngle) < Math.abs(endAngle - startAngle);
  if (!between) {
    endAngle += endAngle > startAngle ? -2 * Math.PI : 2 * Math.PI;
  }
  const sweep = endAngle - startAngle;
  const samples = Math.max(MIN_ARC_SAMPLES, Math.ceil(Math.abs(sweep) * SAMPLES_PER_RADIAN));
  const points: Vec2[] = [];
  const cumulative: number[] = [];
  let total = 0;
  let prev: Vec2 | null = null;
  for (let s = 0; s <= samples; s++) {
    const t = s / samples;
    const angle = startAngle + sweep * t;
    const sample: Vec2 = {
      x: centre.x + Math.cos(angle) * radius,
      y: centre.y + Math.sin(angle) * radius,
    };
    if (prev !== null) {
      total += Math.hypot(sample.x - prev.x, sample.y - prev.y);
    }
    points.push(sample);
    cumulative.push(total);
    prev = sample;
  }
  return { points, cumulativeLength: cumulative, length: total };
}

/**
 * Circumcentre of the triangle (a, b, c). Returns `null` if the
 * three points are collinear or coincident (zero area triangle).
 */
function circumcentre(a: Vec2, b: Vec2, c: Vec2): Vec2 | null {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-9) {
    return null;
  }
  const aSq = a.x * a.x + a.y * a.y;
  const bSq = b.x * b.x + b.y * b.y;
  const cSq = c.x * c.x + c.y * c.y;
  return {
    x: (aSq * (b.y - c.y) + bSq * (c.y - a.y) + cSq * (a.y - b.y)) / d,
    y: (aSq * (c.x - b.x) + bSq * (a.x - c.x) + cSq * (b.x - a.x)) / d,
  };
}

/** Bring `angle` into the same revolution as `reference` (within ±π of it). */
function unwrapAngle(angle: number, reference: number): number {
  while (angle - reference > Math.PI) {
    angle -= 2 * Math.PI;
  }
  while (angle - reference < -Math.PI) {
    angle += 2 * Math.PI;
  }
  return angle;
}

/**
 * Catmull-Rom spline: each consecutive 4-tuple
 * `(P_{i-1}, P_i, P_{i+1}, P_{i+2})` defines a segment between
 * `P_i` and `P_{i+1}`. Endpoints are duplicated for boundary
 * conditions (so the first segment uses `(P_0, P_0, P_1, P_2)`
 * and the last uses `(P_{n-2}, P_{n-1}, P_n, P_n)`).
 *
 * Legacy format — most modern .osu maps use B for everything; C
 * shows up in pre-2014 ranked sets.
 */
function buildCatmullPath(cps: ReadonlyArray<Vec2>): SliderPath {
  if (cps.length < 2) {
    return emptyPath(cps[0]);
  }
  const points: Vec2[] = [{ x: cps[0].x, y: cps[0].y }];
  const cumulative: number[] = [0];
  let total = 0;
  let prev = cps[0];
  for (let i = 0; i < cps.length - 1; i++) {
    const p0 = i === 0 ? cps[0] : cps[i - 1];
    const p1 = cps[i];
    const p2 = cps[i + 1];
    const p3 = i + 2 < cps.length ? cps[i + 2] : cps[cps.length - 1];
    for (let s = 1; s <= SAMPLES_PER_SEGMENT; s++) {
      const t = s / SAMPLES_PER_SEGMENT;
      const sample = evaluateCatmull(p0, p1, p2, p3, t);
      total += Math.hypot(sample.x - prev.x, sample.y - prev.y);
      points.push(sample);
      cumulative.push(total);
      prev = sample;
    }
  }
  return { points, cumulativeLength: cumulative, length: total };
}

/**
 * Standard Catmull-Rom spline evaluation (uniform parameterisation,
 * tension 0.5).
 */
function evaluateCatmull(p0: Vec2, p1: Vec2, p2: Vec2, p3: Vec2, t: number): Vec2 {
  const tt = t * t;
  const ttt = tt * t;
  const x =
    0.5 *
    (2 * p1.x +
      (-p0.x + p2.x) * t +
      (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * tt +
      (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * ttt);
  const y =
    0.5 *
    (2 * p1.y +
      (-p0.y + p2.y) * t +
      (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * tt +
      (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * ttt);
  return { x, y };
}

/**
 * Parse the curve payload from an .osu `[HitObjects]` row's slider
 * field, e.g. `B|100:100|150:150|200:200`. Returns the curve type
 * and the post-head control points.
 *
 * Returns `null` for malformed input so callers can degrade
 * gracefully (e.g. treat the slider as a head-only circle).
 */
export function parseSliderCurveField(
  field: string,
): { curveType: SliderCurveType; controlPoints: Vec2[] } | null {
  const parts = field.split('|');
  if (parts.length < 2) {
    return null;
  }
  const head = parts[0].trim().toUpperCase();
  if (head !== 'L' && head !== 'B' && head !== 'P' && head !== 'C') {
    return null;
  }
  const controlPoints: Vec2[] = [];
  for (let i = 1; i < parts.length; i++) {
    const xy = parts[i].split(':');
    if (xy.length !== 2) {
      return null;
    }
    const x = Number(xy[0]);
    const y = Number(xy[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }
    controlPoints.push({ x, y });
  }
  return { curveType: head, controlPoints };
}
